import axiosClient from "../axiosClient";
import { loadEasterBalance, readEasterBalance } from "../economy/api_v1_economy";
import {
  announceEggReward,
  claimedRewardIsNew,
  hasRewardBaseline,
  rememberClaimedRewards,
  rewardTaskId,
} from "../../utils/eggReward";
import { writeListCache } from "../../utils/sessionListCache";
import { mythFactAnswerWasWrong } from "../../utils/mythFactOutcome";
import {
  CACHE_KEYS,
  SESSION_CACHE_TTL,
  cachedRequest,
  invalidateCachedRequest,
} from "../../utils/apiCache";

export const CHALLENGES_LIST_CACHE_KEY = "ga_challenges_list_cache";

function withBearer() {
  const bearerTokenHeader = sessionStorage.getItem("accessToken");
  axiosClient.defaults.headers.common["Authorization"] =
    `Bearer ${bearerTokenHeader}`;
  return axiosClient;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function unwrap(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (
    !Array.isArray(payload.tasks) &&
    payload.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }
  return payload;
}

function extractTasks(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  const root = unwrap(payload);
  if (!root || typeof root !== "object") return [];
  const list = root.tasks || (Array.isArray(root.data) ? root.data : null);
  return Array.isArray(list) ? list : [];
}

export function getChallenges(options = {}) {
  return cachedRequest(
    CACHE_KEYS.challenges,
    () => withBearer().get("/challenges"),
    { ttl: SESSION_CACHE_TTL, force: options.force === true },
  );
}

export function normalizeChallengeTask(task, index = 0) {
  if (!task || typeof task !== "object") return null;

  return {
    taskKey: task.task_key ?? task.taskKey ?? null,
    type: String(task.type || ""),
    title: String(task.title || ""),
    description: task.description == null ? null : String(task.description),
    rewardEggs: Math.max(0, toNumber(task.reward_eggs ?? task.rewardEggs, 0)),
    claimed: Boolean(task.claimed),
    position: index,
  };
}

export function normalizeChallenges(payload) {
  const root = unwrap(payload);
  if (!root || payload?.success === false || root.success === false) return null;

  const tasks = applyChallengeCompletion(
    extractTasks(payload)
      .map((task, index) => normalizeChallengeTask(task, index))
      .filter(Boolean)
  );

  return {
    success: true,
    tasks,
  };
}

export async function loadChallenges(options = {}) {
  const response = await getChallenges(options);
  const loaded = normalizeChallenges(response?.data);
  if (Array.isArray(loaded?.tasks)) {
    writeListCache(CHALLENGES_LIST_CACHE_KEY, loaded.tasks);
  }
  return loaded;
}

function taskText(task) {
  return `${task?.type || ""} ${task?.title || ""} ${task?.description || ""}`.toLowerCase();
}

export function isWatchShortChallenge(task) {
  const text = taskText(task);
  if (!text.includes("media") || /podcast|listen/.test(text)) return false;
  if (/pro tip/.test(text) && !/short/.test(text)) return false;
  return /short/.test(text) || /tip video/.test(text);
}

export function isLoginChallenge(task) {
  const text = taskText(task);
  return task?.type === "login" || /streak|daily login|\blogin\b/.test(text);
}

export function isQuizChallenge(task) {
  const text = taskText(task);
  return task?.type === "quiz" || /myth|fact|quiz/.test(text);
}

export function isProTipChallenge(task) {
  const text = taskText(task);
  return /pro tip/.test(text) && !/short/.test(text);
}

export function isPuzzleGameChallenge(task) {
  return task?.type === "game" && /puzzle/.test(taskText(task));
}

export function isAimChallenge(task) {
  return task?.type === "game" && /aim/.test(taskText(task));
}

export function isBonusChallenge(task) {
  const text = taskText(task);
  return task?.type === "bonus" || /all tasks|bonus/.test(text);
}

/**
 * Wrong Myth or Fact answer stays open.
 * "Complete all Tasks" is done only when every task above it is done.
 */
export function applyChallengeCompletion(tasks) {
  const list = Array.isArray(tasks) ? tasks : [];
  const wrongQuiz = mythFactAnswerWasWrong();
  const adjusted = list.map((task) => {
    if (wrongQuiz && isQuizChallenge(task) && !isBonusChallenge(task)) {
      return { ...task, claimed: false };
    }
    return task;
  });

  return adjusted.map((task, index) => {
    if (!isBonusChallenge(task)) return task;
    const above = adjusted
      .slice(0, index)
      .filter((item) => !isBonusChallenge(item));
    const allAboveDone =
      above.length > 0 && above.every((item) => item.claimed);
    return { ...task, claimed: allAboveDone };
  });
}

/** Bonus reward is available only when every task above that row is claimed. */
export function isBonusUnlocked(tasks) {
  const list = applyChallengeCompletion(tasks);
  const bonusIndex = list.findIndex((task) => isBonusChallenge(task));
  if (bonusIndex < 0) return false;
  const above = list
    .slice(0, bonusIndex)
    .filter((task) => !isBonusChallenge(task));
  return above.length > 0 && above.every((task) => task.claimed);
}

/**
 * Show egg modal only when this challenge just became claimed.
 * claimed:false → reward still available (modal after completion)
 * claimed:true already → reward already taken (no modal)
 */
function announceIfRewardLanded(task, eggBalance) {
  if (!task?.claimed || !(task.rewardEggs > 0)) return false;
  if (!claimedRewardIsNew(task)) return false;
  announceEggReward({
    title: task.title,
    rewardEggs: task.rewardEggs,
    eggBalance,
    taskKey: rewardTaskId(task),
  });
  return true;
}

export async function presentChallengeReward(match) {
  if (typeof match !== "function") return null;

  if (!hasRewardBaseline()) {
    try {
      const prior = await loadChallenges();
      rememberClaimedRewards(prior?.tasks);
    } catch (error) {
      console.error("Challenge snapshot failed:", error);
      return null;
    }
  }

  const loaded = await refreshChallenges();
  const after = readEasterBalance();
  const tasks = loaded?.tasks || [];
  announceIfRewardLanded(tasks.find(match), after);
  if (match !== isBonusChallenge) {
    announceIfRewardLanded(tasks.find(isBonusChallenge), after);
  }
  return loaded;
}

export async function refreshChallenges() {
  invalidateCachedRequest(CACHE_KEYS.challenges);
  invalidateCachedRequest(CACHE_KEYS.easters);
  const loaded = await loadChallenges({ force: true });
  window.dispatchEvent(new CustomEvent("challengeClaimed"));
  try {
    await loadEasterBalance({ force: true });
  } catch (error) {
    console.error("Easter wallet refresh failed:", error);
  }
  return loaded;
}
